import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import {
  AnalyticsService,
  DuetDatePickerErrorMsg,
  HighchartsThemeService,
  ISOYearMonth,
  ResponsiveService,
  ToggleButton,
} from '@frk/eds-components';
import { toLocaleNumber } from '@utils/il8n/number-il8n';
import {
  ChartCalculationType,
  PerformanceDateRange,
  FundPerformanceCumulativeGraphData,
  PerformanceDateRangeSelection,
} from '@products/types/performance.type';
import { TranslateService } from '@shared/translate/translate.service';
import { subtractYears } from '@utils/text/date-utils';
import { API_DATE_FORMAT } from '@products/utils/constants/product.constants';
import dayjs from 'dayjs';
import {
  getDatePickerErrorMsg,
  momentMonthYearFormat,
  setHighChartLangOptions,
} from '@utils/il8n/moment-il8n';
import {
  ANALYTICS_DATE_EVENT,
  ANALYTICS_DATE_FORMAT,
  ANALYTICS_DETAILED_DATE_EVENT,
  DATE_FORMAT_STD,
  DATE_FORMAT_YEAR_MONTH_STD,
} from '@utils/app.constants';
import { SiteConfigService } from '@services/site-config.service';
import { Observable } from 'rxjs';

const logger = Logger.getLogger('FundPerformanceCumulativeGraphComponent');

@Component({
  selector: 'ft-fund-performance-cumulative-graph',
  templateUrl: './fund-performance-cumulative-graph.component.html',
  styleUrls: ['./fund-performance-cumulative-graph.component.scss'],
})
export class FundPerformanceCumulativeGraphComponent
  implements OnInit, OnChanges {
  highcharts;

  @Input() chartCalculationOptions: ChartCalculationType[];

  @Input() selectedChartCalculation: ChartCalculationType;

  @Input() valuePrefix = '';

  @Input() seriesData: Record<
    ChartCalculationType,
    FundPerformanceCumulativeGraphData
  >;

  @Input() graphNames: string[];

  @Input() dateRange: PerformanceDateRange;

  @Input() customHistory: boolean;

  @Input() isdatePickerAnalyticsEnabled = false; // For Disabling Analytics for Date Selection

  // @Input() fundId: FundId;

  @Output()
  dateRangeSelectionEvent = new EventEmitter<PerformanceDateRangeSelection>();

  public datePickerFormat: string = momentMonthYearFormat();

  yearGroups: ToggleButton[];

  chartCalculations: ToggleButton[];

  chartOptions: Highcharts.Options;

  minDate: string;
  maxDate: string;
  fromDate: string;
  toDate: string;
  minDateYearMonthStd: ISOYearMonth;
  maxDateYearMonthStd: ISOYearMonth;
  fromDateYearMonthStd: ISOYearMonth;
  toDateYearMonthStd: ISOYearMonth;
  currentSeparator = '.';

  private noChartDataLabel = 'no-chart-data';
  private selectedYearGroup = 'sinceInception';
  private isCustomRangeSelected = false;

  public isMobile$: Observable<boolean>;
  public errorMsgObj: DuetDatePickerErrorMsg;

  private currentSelectedToggle: ToggleButton;
  @Input() show6m15yr = true;
  constructor(
    private translateService: TranslateService,
    private analytics: AnalyticsService,
    private highchartsTheme: HighchartsThemeService,
    private siteConfig: SiteConfigService,
    protected responsiveService: ResponsiveService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.errorMsgObj = getDatePickerErrorMsg(this.translateService);
  }

  ngOnInit(): void {
    import(/* webpackChunkName: "highcharts-async" */ 'highcharts').then(
      (highchartsModule) => {
        highchartsModule.setOptions({
          lang: setHighChartLangOptions(this.siteConfig.getPdsLanguage()),
        });
        highchartsModule.setOptions(this.highchartsTheme.themeOptions);
        this.highcharts = highchartsModule;
        this.init();
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.seriesData) {
      this.updateChartOptions();
      this.maxDate = this.dateRange.maxDate;
      this.minDate = this.dateRange.minDate;
      this.toDate = this.isCustomRangeSelected
        ? this.toDate
        : this.dateRange.maxDate;
      this.fromDate = this.isCustomRangeSelected
        ? this.fromDate
        : this.getFromDate();

      this.maxDateYearMonthStd = dayjs(this.maxDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      this.minDateYearMonthStd = dayjs(this.minDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      this.toDateYearMonthStd = dayjs(this.toDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      this.fromDateYearMonthStd = dayjs(this.fromDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
    }
  }

  private init() {
    this.updateChartOptions();

    this.yearGroups = this.getYearGroups();
  }

  onYearGroupUpdate(selectedToggle: ToggleButton) {
    if (
      !this.currentSelectedToggle ||
      this.currentSelectedToggle.text !== selectedToggle?.text
    ) {
      this.selectedYearGroup = selectedToggle.value;
      this.fromDate = this.getFromDate();
      this.toDate = this.dateRange.maxDate;
      this.toDateYearMonthStd = dayjs(this.toDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      this.fromDateYearMonthStd = dayjs(this.fromDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      this.isCustomRangeSelected = false;
      this.updateChartOptions();
      this.currentSelectedToggle = selectedToggle;
    }
  }

  reset(event): void {
    event.preventDefault();
    this.fromDate = this.getFromDate();
    this.toDate = this.dateRange.maxDate;
    this.toDateYearMonthStd = dayjs(this.toDate, DATE_FORMAT_STD).format(
      DATE_FORMAT_YEAR_MONTH_STD
    ) as ISOYearMonth;
    this.fromDateYearMonthStd = dayjs(this.fromDate, DATE_FORMAT_STD).format(
      DATE_FORMAT_YEAR_MONTH_STD
    ) as ISOYearMonth;
    this.isCustomRangeSelected = false;
    this.updateChartOptions();
  }
  onChartCalculationUpdate(selectedToggle: ToggleButton) {
    this.selectedChartCalculation = selectedToggle.value as ChartCalculationType;
    this.updateChartOptions();
  }

  setCustomDateRange(inputType: string, selectedMonth: ISOYearMonth): void {
    // Handle when entered via keyboard and existing is delete
    if (!selectedMonth) {
      return;
    }
    const date = dayjs(selectedMonth, DATE_FORMAT_YEAR_MONTH_STD);
    const selectedDate = date.format();

    if (inputType === 'fromDate') {
      this.fromDate = selectedDate;
      this.fromDateYearMonthStd = dayjs(this.fromDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      /** Only for custom dropdown change we need to fire api call not for clicking all tabs */
      if (this.selectedYearGroup === '') {
        this.isCustomRangeSelected = true;
        this.dateRangeSelectionEvent.next({
          fromDate: selectedDate,
          toDate: this.toDate,
        });
      }
    } else {
      this.toDate = selectedDate;
      this.toDateYearMonthStd = dayjs(this.toDate, DATE_FORMAT_STD).format(
        DATE_FORMAT_YEAR_MONTH_STD
      ) as ISOYearMonth;
      /** Only for custom dropdown change we need to fire api call not for clicking all tabs */
      if (this.selectedYearGroup === '') {
        this.isCustomRangeSelected = true;
        this.dateRangeSelectionEvent.next({
          fromDate: this.fromDate,
          toDate: selectedDate,
        });
      }
    }
    if (this.isdatePickerAnalyticsEnabled) {
      this.isdatePickerAnalyticsEnabled = false;
      this.analytics.trackEvent({
        event: ANALYTICS_DATE_EVENT,
        detailed_event: ANALYTICS_DETAILED_DATE_EVENT,
        action: 'Date Range',
        label: `${dayjs(this.fromDate).format(ANALYTICS_DATE_FORMAT)} - ${dayjs(
          this.toDate
        ).format(ANALYTICS_DATE_FORMAT)}`,
        event_data: {
          date_type: ANALYTICS_DATE_FORMAT,
          date_value: `${dayjs(this.fromDate).format(
            ANALYTICS_DATE_FORMAT
          )} - ${dayjs(this.toDate).format(ANALYTICS_DATE_FORMAT)}`,
        },
      });
    }
  }

  private updateChartOptions() {
    this.getChartOptions();
    this.chartOptions.series = this.getSeries();
    // cloned to trigger onChange
    this.chartOptions = cloneDeep(this.chartOptions);
  }

  private getSeries() {
    const chartCalculation = this.selectedChartCalculation
      ? this.selectedChartCalculation
      : ChartCalculationType.HUNDRED_REBASE_POINTS;
    const graphData = this.seriesData?.[chartCalculation];
    const chartSerieses = [];
    const selectedPeriod = this.isCustomRangeSelected
      ? 'custom'
      : this.selectedYearGroup;
    let chartIndex = 0;
    if (graphData !== undefined && graphData[selectedPeriod]) {
      for (const thisSeriesData of graphData[selectedPeriod]) {
        const thisSeries = [];

        thisSeriesData.forEach((el) => {
          if (el[2]) {
            this.currentSeparator = el[1];
          }
          thisSeries.push([el[0], el[2]]);
        });
        const thisObj = {
          name: this.graphNames[chartIndex],
          type: 'line',
          data: thisSeries,
        };
        chartIndex++;
        chartSerieses.push(thisObj);
      }
      if (this.selectedYearGroup === 'sinceInception') {
        this.selectedYearGroup =
          !graphData?.sinceInception ||
          !this.isDataAvailableForYear(graphData.sinceInception)
            ? 'oneYear'
            : 'sinceInception';
      }
    }
    return chartSerieses;
  }

  private getYearGroups(): ToggleButton[] {
    const chartCalculation = this.selectedChartCalculation
      ? this.selectedChartCalculation
      : ChartCalculationType.HUNDRED_REBASE_POINTS;
    const graphData = this.seriesData?.[chartCalculation];
    const yearGroups = [
      {
        text: this.translateService.instant('products.price-toggle-1-year'),
        active: this.selectedYearGroup === 'oneYear',
        value: 'oneYear',
        disabled:
          !graphData?.oneYear ||
          !this.isDataAvailableForYear(graphData.oneYear),
      },
      {
        text: this.translateService.instant('products.price-toggle-3-years'),
        active: this.selectedYearGroup === 'threeYear',
        value: 'threeYear',
        disabled:
          !graphData?.threeYear ||
          !this.isDataAvailableForYear(graphData.threeYear),
      },
      {
        text: this.translateService.instant('products.price-toggle-5-years'),
        active: this.selectedYearGroup === 'fiveYear',
        value: 'fiveYear',
        disabled:
          !graphData?.fiveYear ||
          !this.isDataAvailableForYear(graphData.fiveYear),
      },
      {
        text: this.translateService.instant('products.price-toggle-10-years'),
        active: this.selectedYearGroup === 'tenYear',
        value: 'tenYear',
        disabled:
          !graphData?.tenYear ||
          !this.isDataAvailableForYear(graphData.tenYear),
      },
    ];
    if (this.show6m15yr) {
      yearGroups.push({
        text: this.translateService.instant('products.price-toggle-15-years'),
        active: this.selectedYearGroup === 'fifteenYear',
        value: 'fifteenYear',
        disabled:
          !graphData?.fifteenYear ||
          !this.isDataAvailableForYear(graphData.fifteenYear),
      });
    }
    yearGroups.push({
      text: this.translateService.instant('pds.CUMULATIVE_RET_ACTINC'),
      active: this.selectedYearGroup === 'sinceInception',
      value: 'sinceInception',
      disabled:
        !graphData?.sinceInception ||
        !this.isDataAvailableForYear(graphData.sinceInception),
    });

    return yearGroups;
  }

  private isDataAvailableForYear(graphData: any[]): boolean {
    return graphData?.length >= 1 && graphData[0]?.length > 0;
  }

  private getFromDate() {
    const standardDate = this.dateRange.maxDate;
    switch (this.selectedYearGroup) {
      case 'threeYear':
        return subtractYears(standardDate, API_DATE_FORMAT, 3);
      case 'fiveYear':
        return subtractYears(standardDate, API_DATE_FORMAT, 5);
      case 'tenYear':
        return subtractYears(standardDate, API_DATE_FORMAT, 10);
      case 'fifteenYear':
        return subtractYears(standardDate, API_DATE_FORMAT, 15);
      case 'sinceInception':
      // empty case defined for the custom selection of dropdown
      case '':
        return this.dateRange.minDate;
      default:
        return subtractYears(standardDate, API_DATE_FORMAT, 1);
    }
  }

  private getChartOptions(): void {
    const chartCalculation = this.selectedChartCalculation
      ? this.selectedChartCalculation
      : ChartCalculationType.HUNDRED_REBASE_POINTS;
    const valPrefix =
      chartCalculation === ChartCalculationType.TENK_GROWTH
        ? this.valuePrefix
        : '';
    const highchartsModule = this.highcharts;
    this.chartOptions = {
      title: null,
      series: [],
      chart: {
        spacingLeft: 0,
        spacingRight: 0,
        marginRight: 20,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          overflow: 'allow',
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
        },
      },
      yAxis: {
        gridLineDashStyle: 'LongDash',
        title: {
          text: null,
        },
        offset: -5,
        labels: {
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
          formatter() {
            let label = this.value;
            if (label > 999) {
              label = toLocaleNumber(this.value, 'standard', true);
              return valPrefix + label.toString();
            }
            return valPrefix + label.toString();
          },
        },
        allowDecimals: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          // fillOpacity: 0,
          marker: {
            enabled: false,
          },
        },
      },
      tooltip: {
        enabled: true,
        valuePrefix: valPrefix,
        valueDecimals: 2,
        formatter() {
          return `${this.series.name}: <b>${toLocaleNumber(
            this.point.y,
            'standard',
            false
          )}</b><br/>`;
        },
      },
      lang: {
        noData: this.translateService.instant(this.noChartDataLabel),
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: '#a7a7a7',
        },
      },
    };
  }

  // enable date picker analytics once user change the date
  onOpenMonthPicker(value: string) {
    if (value === 'open') {
      this.isdatePickerAnalyticsEnabled = true;
    }
  }

  OnMonthSelected(monthSelected: boolean) {
    if (monthSelected) {
      this.selectedYearGroup = '';
      this.yearGroups = this.getYearGroups();
    }
  }
}
